import {createContext, memo, ReactElement, useContext, useEffect, useState} from "react";
import {v4} from "uuid";
import CrossIcon from '../component/icons/svg/cross-8x8.svg?react'

import './NotificationList.scss'
import {useTranslation} from "react-i18next";
import AppContext from "./AppContext";
import {escapeHtml} from "../helper/escapeHtml";

type Props = {
  children: ReactElement
}

export type AppNotification = {
  id: string
  title: string|null
  message: string
  persistent: boolean
  ttl: number
  actions?: Array<{
    label: string
    onClick: () => void
  }>
}

export type AppNotificationCreateAttributes = {
  message: string
  title?: string
  id?: string
  persistent?: boolean
  ttl?: number
  actions?: Array<{
    label: string
    onClick: () => void
  }>
}

type NotificationContextType = {
  notifications: AppNotification[],
  createNotification: (attributes: AppNotificationCreateAttributes) => string,
  hideNotification: (id: string) => void,
  clearNotifications: () => void,
  // Bumped by clearNotifications: list it in an effect's deps to re-raise a toast that is still valid.
  clearGeneration: number,
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  createNotification: () => '',
  hideNotification: () => null,
  clearNotifications: () => null,
  clearGeneration: 0,
})

const NotificationContextProvider = (props: Props) => {
  const {t} = useTranslation()

  const {
    checkedIn,
    conferenceStatus,
  } = useContext(AppContext)

  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [clearGeneration, setClearGeneration] = useState(0)

  useEffect(() => {
    const onSwMessage = (ev: any) => {
      if (ev.data.type === 'notification') {
        const data = ev.data.notification

        // RISKS.md/P0.7: everything here is attacker-controlled — a push payload carries a display
        // name the sender typed in the green room, and the body below renders through
        // dangerouslySetInnerHTML with i18next's own escaping switched off. Escape the PLACEHOLDER
        // VALUES rather than the finished string, so catalogue markup (`<i>{{name}}</i>`) survives
        // while an injected tag does not.
        if (typeof data.messageCode !== 'undefined') {
          const placeholders: Record<string, string> = {}
          Object.keys(data.placeholders || {}).forEach(k => {
            placeholders[k] = escapeHtml(String(data.placeholders[k]))
          })
          data.message = t(data.messageCode, placeholders)
        } else if (typeof data.message === 'string') {
          // Server-rendered text (e.g. the admit push), which interpolates a name of its own.
          data.message = escapeHtml(data.message)
        }

        createNotification(ev.data.notification)
      }
    }
    navigator.serviceWorker.addEventListener('message', onSwMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage)
  }, []);

  useEffect(() => {
    const hasWaitingNotification = !!notifications.find(n => n.id === 'admitted-waiting')
    if (hasWaitingNotification && (!checkedIn || conferenceStatus.admitted)) {
      hideNotification('admitted-waiting')
    }
  }, [checkedIn, conferenceStatus]);

  const createNotification = (attributes: AppNotificationCreateAttributes) => {
    const notification: AppNotification = {
      id: attributes.id || v4(),
      title: attributes.title || null,
      message: attributes.message,
      persistent: attributes.persistent || false,
      ttl: attributes.ttl || 0,
      actions: attributes.actions,
    }

    // Functional updates: create AND hide can run in the SAME effect (a moderator effect toasts a
    // newly-waiting user, then hides the toast for someone just admitted). With a captured array
    // these batched setState calls clobber each other — the last non-functional write wins, so the
    // just-created toast is lost. A functional updater composes them correctly. De-dupe by id so a
    // notice created from more than one place (e.g. 'admitted-waiting') never stacks a duplicate.
    setNotifications(prev => prev.some(n => n.id === notification.id) ? prev : [...prev, notification])

    return notification.id
  }

  const hideNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }

  const clearNotifications = () => {
    setNotifications([])
    setClearGeneration(n => n + 1)
  }

  const value = {
    notifications,
    createNotification,
    hideNotification,
    clearNotifications,
    clearGeneration,
  }

  return (
    <NotificationContext.Provider value={value}>
      { props.children }
    </NotificationContext.Provider>
  )
}

const Notification = (props: { notification: AppNotification, onClick: () => void }) => {
  const { notification } = props;
  // Visibility is STATE, not imperative DOM class toggling. The meeting re-renders constantly
  // (live-status churn); the old code removed the `hidden` class on the DOM node while every
  // render re-added it via className, so a re-render instantly re-hid the toast — invisible in
  // the busy meeting, a sub-second flash in the calmer green room. Deriving the class from state
  // keeps it stable across re-renders.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 100)
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    let removeTimer: ReturnType<typeof setTimeout> | undefined
    if (notification.ttl) {
      hideTimer = setTimeout(() => {
        setVisible(false)
        removeTimer = setTimeout(() => props.onClick(), 1000)
      }, notification.ttl)
    }
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); clearTimeout(removeTimer) }
    // Re-armed on message change: hide-then-create under one id in a single batch keeps this <li>
    // mounted by key, so `[]` would let the replacement inherit its predecessor's clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification.message, notification.ttl]);

  // Dismiss on the X — works for persistent toasts too (the admit request toast is persistent but
  // must be dismissable). Fades out, then removes.
  const hide = () => {
    setVisible(false)
    setTimeout(() => {
      props.onClick()
    }, 1000);
  }

  const classNames = ["notification"];
  if (!visible) {
    classNames.push("hidden");
  }
  if (!notification.persistent) {
    classNames.push("dismiss");
  }

  return (
    <div className={classNames.join(" ")}>
      <div className="notification__frame">
        <div className="notification__text">
          {notification.title ? (
            <div className="notification__title">{notification.title}</div>
          ) : null}
          <div
            className="notification__body"
            dangerouslySetInnerHTML={{ __html: notification.message }}
          />
        </div>
        {notification.actions && notification.actions.length > 0 && (
          <div className="notification__actions">
            {notification.actions.map((action, i) => (
              <button
                key={i}
                className="notification__action-btn"
                onClick={() => { action.onClick(); props.onClick() }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button className="notification__close" onClick={hide}>
        <CrossIcon/>
      </button>
    </div>
  );
}

const NotificationList = memo(({ filter }: { filter?: (n: AppNotification) => boolean }) => {
  const { notifications, hideNotification } = useContext(NotificationContext);

  const onNotificationClick = (id: string) => {
    hideNotification(id); // Mark notification as hidden (triggers the `hidden` effect)
  };

  // Each view renders its own NotificationList; an optional `filter` scopes which toasts it shows.
  // The green room shows only its own notices (e.g. join errors) — all other snackbars are a
  // meeting-room-only concern.
  const shown = filter ? notifications.filter(filter) : notifications

  return (
    <ul id="app-notifications-wrapper">
      {shown.slice().reverse().map((notification) => (
        <li data-id={notification.id} key={notification.id}>
          <Notification
            notification={notification}
            onClick={() => onNotificationClick(notification.id)}
          />
        </li>
      ))}
    </ul>
  );
})

export {
  NotificationContext as default,
  NotificationContextProvider,
  NotificationList,
}